import { Image } from '../Image';

import { RichText } from 'prismic-reactjs';

type ImageType = {
  imgUrl: string;
  alt?: string;
  caption?: any;
  dimensions?: {
    width: number;
    height: number;
  };
};

type ImageModuleProps = {
  images: ImageType[];
};

export const ImageModule = ({ images }: ImageModuleProps) => {
  const mappedImages = images?.map((image) => ({
    url: image.imgUrl,
    alt: image.alt,
    dimensions: image.dimensions,
    caption: image.caption,
  }));

  if (!images?.length) return null;

  if (images.length === 1) {
    return (
      <Container>
        <div className="flex justify-center">
          <Image
            className="image:rounded-lg image:w-full image:aspect-[4_/_3] h-full grow"
            imageObject={mappedImages[0] ?? null}
            src={mappedImages[0].url}
            alt={mappedImages[0].alt}
            width={mappedImages[0]?.dimensions?.width ?? 688}
            height={mappedImages[0]?.dimensions?.height ?? 280}
            sizes="(min-width: 768px) 60vw, 70vw"
          />
        </div>
        {mappedImages[0].caption && (
          <ImageAuthorCredit>
            <ImageAuthorCreditSpan>Image credit:</ImageAuthorCreditSpan>
            <RichText render={images[0].caption} />
          </ImageAuthorCredit>
        )}
      </Container>
    );
  }
  if (mappedImages.length === 2) {
    return (
      <Container>
        <div className="grid grid-cols-2 justify-center gap-2 md:gap-4">
          <Image
            className="image:rounded-lg image:object-cover image:w-full image:h-full image:aspect-[6_/_5] grow"
            imageObject={mappedImages[0] ?? null}
            src={mappedImages[0].url}
            alt={mappedImages[0].alt}
            width={336}
            height={280}
            sizes="(min-width: 768px) 30vw,  45vw"
          />
          <Image
            className="image:rounded-lg image:object-cover image:w-full image:h-full image:h-full h-full grow"
            imageObject={mappedImages[1] ?? null}
            src={mappedImages[1].url}
            alt={mappedImages[1].alt}
            width={336}
            height={280}
            sizes="(min-width: 768px) 30vw,  45vw"
          />
        </div>
        <div className="flex flex-wrap justify-end">
          {mappedImages[0].caption && (
            <div className="text-neutral-faded [&>a]:text-neutral-faded [&>a]:hover:text-link mt-2 flex text-xs md:text-sm [&>a]:text-xs [&>a]:md:text-sm">
              <span className="mx-1">image 1</span>
              <RichText render={mappedImages[0].caption} />,
            </div>
          )}
          {mappedImages[1].caption && (
            <ImageAuthorCredit>
              <ImageAuthorCreditSpan>image 2</ImageAuthorCreditSpan>
              <RichText render={mappedImages[1].caption} />
            </ImageAuthorCredit>
          )}
        </div>
      </Container>
    );
  }
  if (mappedImages.length >= 3) {
    return (
      <Container>
        <div className="grid grid-cols-3 grid-rows-2 justify-center gap-2 md:gap-4">
          <Image
            className="image:rounded-lg image:object-cover image:w-full image:h-full image:aspect-[3_/_2] col-start-1 col-end-3 row-start-1 row-end-3 h-full grow self-center"
            imageObject={mappedImages[0] ?? null}
            src={mappedImages[0].url}
            alt={mappedImages[0].alt}
            width={400}
            height={280}
            sizes="(min-width: 768px) 50vw, 65vw"
          />
          <Image
            className="image:rounded-lg image:object-cover image:w-full image:h-full image:aspect-[3_/_2] col-start-3 col-end-auto row-start-1 row-end-auto h-full grow self-center"
            imageObject={mappedImages[1] ?? null}
            src={mappedImages[1].url}
            alt={mappedImages[1].alt}
            width={272}
            height={132}
            sizes="(min-width: 768px) 15vw, 25vw"
          />
          <Image
            className="image:rounded-lg image:object-cover image:w-full image:h-full image:aspect-[3_/_2] col-start-3 col-end-auto row-start-2 row-end-auto h-full grow self-center"
            imageObject={mappedImages[2] ?? null}
            src={mappedImages[2].url}
            alt={mappedImages[2].alt}
            width={272}
            height={132}
            sizes="(min-width: 768px) 15vw, 25vw"
          />
        </div>
        <ImageCredit>
          {images[0].caption && (
            <ImageAuthorCredit>
              <ImageAuthorCreditSpan>image 1</ImageAuthorCreditSpan>
              <RichText render={images[0].caption} />,
            </ImageAuthorCredit>
          )}

          {images[1].caption && (
            <ImageAuthorCredit>
              <ImageAuthorCreditSpan>image 2</ImageAuthorCreditSpan>
              <RichText render={images[1].caption} />,
            </ImageAuthorCredit>
          )}

          {images[2].caption && (
            <ImageAuthorCredit>
              <ImageAuthorCreditSpan>image 3</ImageAuthorCreditSpan>
              <RichText render={images[2].caption} />
            </ImageAuthorCredit>
          )}
        </ImageCredit>
      </Container>
    );
  }
  return null;
};

const Container = ({ children, ...rest }) => (
  <div className="my-6" {...rest}>
    {children}
  </div>
);
const ImageCredit = ({ children, ...rest }) => (
  <div className="flex flex-wrap justify-end" {...rest}>
    {children}
  </div>
);

const ImageAuthorCredit = ({ children, ...rest }) => (
  <div
    className="text-neutral-faded [&>a]:text-neutral-faded [&>a]:hover:text-link mt-2 flex text-xs md:text-sm [&>a]:text-xs [&>a]:md:text-sm"
    {...rest}
  >
    {children}
  </div>
);

const ImageAuthorCreditSpan = ({ children, ...rest }) => (
  <span className="mx-1 flex" {...rest}>
    {children}
  </span>
);
